/**
 * Deterministic guardrails for Ask Kai task creation.  This stays free of DOM
 * and Worker globals so the client, Worker, and unit tests share one meaning.
 */

import { projectNameAliases } from './kai-query-shared.js';

export function normalizeAskKaiSemanticText(value = '') {
    return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim()
        .replace(/\s+/g, ' ');
}

const START_PHRASES = [
    'start date', 'starts', 'start', 'starting', 'begin date', 'begins', 'beginning',
    'kickoff', 'scheduled to start', 'schedule start', 'fecha de inicio',
    'fecha de comienzo', 'fecha de arranque', 'empieza', 'empiece', 'comienza', 'inicia', 'arranca'
];
const END_PHRASES = [
    'end date', 'due date', 'deadline', 'due', 'ending', 'ends', 'finish by',
    'finished by', 'complete by', 'completed by', 'fecha de fin',
    'fecha de finalizacion', 'fecha de termino', 'fecha de vencimiento',
    'fecha limite', 'vencimiento', 'vence', 'venza', 'debe vencer', 'terminada',
    'debe completarse'
];

function containsPhrase(text, phrase) {
    return ` ${text} `.includes(` ${phrase} `);
}

function phrasePositions(text, phrases) {
    return phrases.flatMap((phrase) => {
        const positions = [];
        let offset = text.indexOf(phrase);
        while (offset !== -1) {
            positions.push(offset);
            offset = text.indexOf(phrase, offset + phrase.length);
        }
        return positions;
    });
}

function relativeDates(text, dateContext = {}) {
    return [
        ...phrasePositions(text, ['tomorrow', 'manana']).map((position) => ({ position, value: dateContext.TOMORROW_DATE_ISO || null })),
        ...phrasePositions(text, ['today', 'hoy']).map((position) => ({ position, value: dateContext.CURRENT_DATE_ISO || null }))
    ].filter(({ value }) => value);
}

function closestRelativeHint(relative, positions) {
    if (!positions.length) return null;
    return relative
        .map((date) => ({ ...date, distance: Math.min(...positions.map((position) => Math.abs(position - date.position))) }))
        .sort((a, b) => a.distance - b.distance || a.position - b.position)[0]?.value || null;
}

/** Identifies explicit schedule fields and safely anchors hoy/today/tomorrow/mañana. */
export function analyzeAskKaiSchedule(instruction = '', dateContext = {}) {
    const text = normalizeAskKaiSemanticText(instruction);
    const requiresStartDate = START_PHRASES.some((phrase) => containsPhrase(text, phrase));
    const requiresEndDate = END_PHRASES.some((phrase) => containsPhrase(text, phrase))
        || /\b(?:finish|complete|completed)(?:\s+\p{L}+){0,2}\s+by\b/u.test(text)
        || /\b(?:by|until|through|hasta\s+el)\s+\d{1,2}[\/-]\d{1,2}\b/u.test(text);
    const startPositions = phrasePositions(text, START_PHRASES);
    const endPositions = phrasePositions(text, END_PHRASES);
    const relative = relativeDates(text, dateContext);
    return {
        requiresStartDate,
        requiresEndDate,
        startDateHint: requiresStartDate ? closestRelativeHint(relative, startPositions) : null,
        endDateHint: requiresEndDate ? closestRelativeHint(relative, endPositions) : null
    };
}

const PROJECT_REFERENCE_PATTERN = /(?:\b(?:dentro\s+del?|en\s+el|para\s+el|within|under|inside|in\s+the)\s+)?\b(?:proyecto|project|objetivo|objective|obj)\.?\s+([^!?;,\n]+)/iu;

function deriveProjectAliases(name = '') {
    return [...new Set(projectNameAliases(name).map(normalizeAskKaiSemanticText).filter(Boolean))];
}

function hasCompleteTokenPhrase(haystack, needle) {
    return ` ${haystack} `.includes(` ${needle} `);
}

export function extractAskKaiProjectReference(instruction = '') {
    const match = String(instruction || '').match(PROJECT_REFERENCE_PATTERN);
    if (!match) return null;
    const rawReference = String(match[1] || '').trim().replace(/[.]+$/, '').trim();
    return rawReference || null;
}

/**
 * Resolve only a clearly stated project/objective reference.  Never depends on
 * project order: ties remain ambiguous instead of picking an arbitrary project.
 */
export function resolveAskKaiProjectReference(instruction = '', projects = []) {
    const rawReference = extractAskKaiProjectReference(instruction);
    const entries = (Array.isArray(projects) ? projects : [])
        .filter((project) => project && project.id != null && String(project.name || '').trim())
        .map((project) => ({ project, aliases: deriveProjectAliases(project.name) }));
    if (!rawReference) {
        const text = normalizeAskKaiSemanticText(instruction);
        const exactFull = entries.filter(({ project }) => hasCompleteTokenPhrase(text, normalizeAskKaiSemanticText(project.name)));
        if (exactFull.length === 1) {
            return { status: 'matched', matched: true, ambiguous: false, projectId: exactFull[0].project.id, rawReference: exactFull[0].project.name, candidates: [{ id: exactFull[0].project.id, name: exactFull[0].project.name }], reason: 'exact_full_name' };
        }
        // Without a "proyecto/objetivo" cue, only a multi-word alias ("para campaña 3")
        // is specific enough to claim; a lone word could be any task title.
        const byAlias = entries.filter(({ aliases }) => aliases.some((alias) => alias.includes(' ') && hasCompleteTokenPhrase(text, alias)));
        if (byAlias.length === 1) {
            const { project } = byAlias[0];
            return { status: 'matched', matched: true, ambiguous: false, projectId: project.id, rawReference: project.name, candidates: [{ id: project.id, name: project.name }], reason: 'exact_alias' };
        }
        return { status: 'not_referenced', matched: false, ambiguous: false };
    }
    // The reference runs to the end of the clause ("campaña 3 que venza mañana"), so
    // try its longest leading phrase that names a project.
    const referenceTokens = normalizeAskKaiSemanticText(rawReference).split(' ');
    const by = (predicate) => entries.filter(predicate);
    let candidates = [];
    let reason = 'no_match';
    for (let length = referenceTokens.length; length > 0 && !candidates.length; length--) {
        const reference = referenceTokens.slice(0, length).join(' ');
        const exactFull = by(({ project }) => normalizeAskKaiSemanticText(project.name) === reference);
        const exactAlias = by(({ aliases }) => aliases.includes(reference));
        const containment = by(({ aliases }) => aliases.some((alias) => hasCompleteTokenPhrase(alias, reference)));
        candidates = exactFull.length ? exactFull : (exactAlias.length ? exactAlias : containment);
        reason = exactFull.length ? 'exact_full_name' : (exactAlias.length ? 'exact_alias' : 'token_containment');
    }
    const candidateDetails = candidates.map(({ project }) => ({ id: project.id, name: project.name }));
    if (candidates.length === 1) {
        return { status: 'matched', matched: true, ambiguous: false, projectId: candidates[0].project.id, rawReference, candidates: candidateDetails, reason };
    }
    return {
        status: candidates.length > 1 ? 'ambiguous' : 'unresolved',
        matched: false,
        ambiguous: candidates.length > 1,
        rawReference,
        candidates: candidateDetails,
        reason: candidates.length > 1 ? reason : 'no_match'
    };
}

export function appendAskKaiUnresolvedProjectNote(description = '', resolution = {}, instruction = '') {
    if (!['ambiguous', 'unresolved'].includes(resolution?.status) || !resolution?.rawReference) return String(description || '').trim();
    const spanish = /\b(crea|tarea|objetivo|proyecto|dentro|para)\b/i.test(String(instruction || ''));
    const note = spanish
        ? `Proyecto/objetivo mencionado: "${resolution.rawReference}". No se asignó automáticamente porque ${resolution.status === 'ambiguous' ? 'coincide con varios proyectos' : 'no coincide con ningún proyecto'}.`
        : `Mentioned project/objective: "${resolution.rawReference}". It was not assigned automatically because ${resolution.status === 'ambiguous' ? 'it matches multiple projects' : 'no project matched it'}.`;
    const existing = String(description || '').trim();
    return existing.includes(note) ? existing : [existing, note].filter(Boolean).join('\n\n');
}
